import { z } from 'zod';

/**
 * Contratto condiviso dei menu IVR (`ivr_menus`), usato da:
 * - route tenant `POST/PUT /api/:slug/voip/ivr-menus` (+ alias `/ivr`);
 * - route integratori `POST/PUT /api/v2/voip/ivr-menus` (API key);
 * - `IvrMenus.tsx` / `IVRFlowBuilder.tsx` (tipo del record e del payload).
 *
 * Il contratto è volutamente NON strict sui blob JSONB (`options`,
 * `timeout_action`, `invalid_action`): le azioni sono JSON prodotto dagli
 * editor (flat DTMF legacy oppure grafo visuale) e interpretato dal dialplan;
 * qui si fissa la forma (tipi primitivi, chiavi note) senza chiudere l'insieme
 * dei valori di `type`/`action`, che il motore ESL estende nel tempo.
 */

/** Valori noti di `timeout_action.type` / `invalid_action.type` / opzione `action`. */
export const IVR_MENU_ACTION_TYPES = [
  'extension', 'voicemail', 'queue', 'ring_group', 'conference', 'ivr', 'submenu',
  'hangup', 'repeat', 'playback', 'external', 'ai_receptionist',
  'internal_ai_agent', 'external_ai_agent',
] as const;
export type IvrMenuActionType = (typeof IVR_MENU_ACTION_TYPES)[number];

export const IVR_FLOW_TYPES = ['static', 'ai_receptionist', 'flow_visual'] as const;
export const IvrFlowTypeSchema = z.enum(IVR_FLOW_TYPES);
export type IvrFlowType = z.infer<typeof IvrFlowTypeSchema>;

export const IVR_TTS_VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'] as const;
export const IvrTtsVoiceSchema = z.enum(IVR_TTS_VOICES);
export type IvrTtsVoice = z.infer<typeof IvrTtsVoiceSchema>;

export const IVR_TTS_LANGUAGES = ['it', 'en', 'de', 'fr', 'es'] as const;
export const IvrTtsLanguageSchema = z.enum(IVR_TTS_LANGUAGES);
export type IvrTtsLanguage = z.infer<typeof IvrTtsLanguageSchema>;

export const IVR_BACKGROUND_MUSIC = ['none', 'pop_leggero', 'jazz_rilassante', 'classica', 'ambient', 'corporate'] as const;
export const IvrBackgroundMusicSchema = z.enum(IVR_BACKGROUND_MUSIC);
export type IvrBackgroundMusic = z.infer<typeof IvrBackgroundMusicSchema>;

/** Azione di timeout / input non valido. */
export const IvrMenuActionSchema = z
  .object({
    type: z.string().min(1).max(64),
    destination: z.string().max(255).default(''),
    timeout: z.number().int().min(0).optional(),
  })
  .passthrough();
export type IvrMenuAction = z.infer<typeof IvrMenuActionSchema>;

/** Opzione DTMF del formato flat legacy (digit → azione). */
export const IvrMenuFlatOptionSchema = z
  .object({
    action: z.string().max(64).optional(),
    destination: z.string().max(255).optional(),
    description: z.string().max(500).optional(),
  })
  .passthrough();
export type IvrMenuFlatOption = z.infer<typeof IvrMenuFlatOptionSchema>;

export const IvrMenuFlatOptionsSchema = z.record(z.string(), IvrMenuFlatOptionSchema);
export type IvrMenuFlatOptions = z.infer<typeof IvrMenuFlatOptionsSchema>;

export const IvrFlowNodeSchema = z
  .object({
    id: z.string(),
    type: z.string(),
    position: z.object({ x: z.number(), y: z.number() }),
    data: z.record(z.string(), z.unknown()),
  })
  .passthrough();
export type IvrFlowNode = z.infer<typeof IvrFlowNodeSchema>;

export const IvrFlowEdgeSchema = z
  .object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    sourceHandle: z.string().nullish(),
    label: z.string().nullish(),
  })
  .passthrough();
export type IvrFlowEdge = z.infer<typeof IvrFlowEdgeSchema>;

/** Grafo del builder visuale (`flow_type = 'flow_visual'`). */
export const IvrFlowGraphSchema = z
  .object({
    nodes: z.array(IvrFlowNodeSchema),
    edges: z.array(IvrFlowEdgeSchema),
  })
  .passthrough();
export type IvrFlowGraph = z.infer<typeof IvrFlowGraphSchema>;

/** `options` sul wire: grafo visuale oppure mappa flat legacy. */
export const IvrMenuOptionsSchema = z.union([IvrFlowGraphSchema, IvrMenuFlatOptionsSchema]);
export type IvrMenuOptions = z.infer<typeof IvrMenuOptionsSchema>;

export function isIvrFlowGraph(options: unknown): options is IvrFlowGraph {
  const o = options as { nodes?: unknown; edges?: unknown } | null | undefined;
  return !!o && typeof o === 'object' && Array.isArray(o.nodes) && Array.isArray(o.edges);
}

const nullableText = (max: number) => z.string().max(max).nullish();

/** Campi scrivibili di `ivr_menus` (colonne reali, tutti opzionali). */
export const IvrMenuWriteFieldsSchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: nullableText(500),
  extension: z.string().trim().min(1).max(20),
  greeting_sound: nullableText(512),
  invalid_sound: nullableText(512),
  exit_sound: nullableText(512),
  goodbye_sound: nullableText(512),
  timeout: z.number().int().min(0).max(600000),
  max_failures: z.number().int().min(0).max(100),
  inter_digit_timeout: z.number().int().min(0).max(60000),
  max_timeouts: z.number().int().min(0).max(100),
  timeout_action: IvrMenuActionSchema,
  invalid_action: IvrMenuActionSchema,
  options: IvrMenuOptionsSchema,
  enabled: z.boolean(),
  background_music: IvrBackgroundMusicSchema,
  tts_voice: IvrTtsVoiceSchema,
  tts_language: IvrTtsLanguageSchema,
  flow_type: IvrFlowTypeSchema,
  ai_agent_id: z.string().uuid().nullish(),
  ai_custom_prompt: nullableText(10000),
  greeting_text: nullableText(5000),
});

/** Body di creazione: `name` ed `extension` obbligatori, resto opzionale. */
export const IvrMenuCreateBodySchema = IvrMenuWriteFieldsSchema
  .partial()
  .required({ name: true, extension: true })
  .passthrough();
export type IvrMenuCreateBody = z.infer<typeof IvrMenuCreateBodySchema>;

/**
 * Body di aggiornamento: tutto opzionale; i campi di identità/audit
 * (`id`, `tenant_id`, `created_at`, `updated_at`) vengono scartati e non
 * raggiungono mai il service, anche se il client li rimanda indietro.
 */
export const IvrMenuUpdateBodySchema = IvrMenuWriteFieldsSchema
  .partial()
  .extend({
    id: z.unknown().optional(),
    tenant_id: z.unknown().optional(),
    created_at: z.unknown().optional(),
    updated_at: z.unknown().optional(),
  })
  .passthrough()
  .transform(({ id: _id, tenant_id: _tenantId, created_at: _createdAt, updated_at: _updatedAt, ...rest }) => rest);
export type IvrMenuUpdateBody = z.infer<typeof IvrMenuUpdateBodySchema>;

/** Record IVR come letto dall'API (`ivr_menus` + audit). */
export const IvrMenuReadSchema = IvrMenuWriteFieldsSchema
  .partial({
    description: true, greeting_sound: true, invalid_sound: true, exit_sound: true,
    goodbye_sound: true, ai_agent_id: true, ai_custom_prompt: true, greeting_text: true,
    background_music: true,
  })
  .extend({
    id: z.string().uuid(),
    tenant_id: z.string().uuid().optional(),
    created_at: z.union([z.string(), z.date()]).optional(),
    updated_at: z.union([z.string(), z.date()]).optional(),
  })
  .passthrough();
export type IvrMenuReadDto = z.infer<typeof IvrMenuReadSchema>;
